import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import { SourcingChannels, SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useMemo } from 'react';
import { useIntl } from 'react-intl';

import EmptyChannelsIcon from '@/assets/icons/empty-channels.svg';
import { EmptyState } from '@/components/ui/empty-state';
import { Separator } from '@/components/ui/separator';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { Sections } from './sections';

import type { SortBy } from './sort';

type SectionKey = 'popular' | 'free' | 'paid';

const SECTION_KEYS: SectionKey[] = ['popular', 'free', 'paid'];

function sectionChannels(
  data: SourcingChannels,
  section: SectionKey,
): SourcingChannels['popular_channels'] {
  if (section === 'popular') return data.popular_channels;
  if (section === 'free') return data.free_channels;
  return data.paid_channels;
}

interface SectionLoaderProps {
  section: SectionKey;
  sectionTitle: string;
  sectionDescription: string;
  searchQuery?: string;
  sortBy?: SortBy;
  initialSelectedOpeningIds: string[];
}

function SectionLoader({
  section,
  sectionTitle,
  sectionDescription,
  searchQuery,
  sortBy,
  initialSelectedOpeningIds,
}: SectionLoaderProps) {
  const { data } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.jobPosting.channels[section],
      initialSelectedOpeningIds,
      searchQuery,
      sortBy,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        initialSelectedOpeningIds,
        sortBy,
        section,
        searchQuery,
      ),
    staleTime: Infinity,
  });

  const channels = sectionChannels(data, section);

  if (channels.count === 0) return null;

  const hasNoVonqChannels = channels.vonq_channels.length === 0;

  return (
    <>
      <Sections
        title={sectionTitle}
        description={sectionDescription}
        channels={channels}
        sectionKey={section}
        isIndeedIntegrationEnabled={data.indeed_integration_enabled}
        forceExpanded={!!searchQuery || hasNoVonqChannels}
      />
      {section !== 'paid' && <Separator />}
    </>
  );
}

interface ChannelsContentProps {
  searchQuery?: string;
  sortBy?: SortBy;
}

export function ChannelsContent({ searchQuery, sortBy }: ChannelsContentProps) {
  const intl = useIntl();
  const { initialSelectedOpeningIds } = useJobPostingContext();

  const sectionMeta = useMemo(
    () => [
      {
        key: 'popular' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Top Picks',
          description: 'Too picked channels for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Boards are selected based on account performance, role type and location.',
          description:
            'Description for top picked channels for posting campaigns',
        }),
      },
      {
        key: 'free' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Free job boards',
          description: 'Free job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            "Boards that post at no cost — no budget, no billing. Includes organic boards and Fountain's own channels like Pool, etc.",
          description: 'Description for free job boards for posting campaigns',
        }),
      },
      {
        key: 'paid' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Paid boards',
          description: 'Paid job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Pay-per-click, CPA, or fixed-duration boards.',
          description: 'Description for paid job boards for posting campaigns',
        }),
      },
    ],
    [intl],
  );

  // Fire all 3 section requests in parallel without suspending. This populates
  // the cache so each SectionLoader's useSuspenseQuery gets a cache hit.
  const results = useQueries({
    queries: SECTION_KEYS.map(section => ({
      queryKey: [
        ...queryKeys.jobPosting.channels[section],
        initialSelectedOpeningIds,
        searchQuery,
        sortBy,
      ],
      queryFn: () =>
        SourcingService.getInternalApiSourcingSourcingChannels(
          initialSelectedOpeningIds,
          sortBy,
          section,
          searchQuery,
        ),
      staleTime: Infinity,
    })),
  });

  const allLoaded = results.every(r => r.isSuccess);
  const allEmpty =
    allLoaded &&
    results.every(
      (r, i) => sectionChannels(r.data!, SECTION_KEYS[i]).count === 0,
    );

  if (allEmpty) {
    const isSearching = !!searchQuery;
    return (
      <div className="border-fx-neutral-200 flex min-h-0 flex-1 items-center justify-center rounded-lg border bg-white p-4 shadow-sm">
        <EmptyState
          icon={EmptyChannelsIcon}
          header={
            isSearching
              ? intl.formatMessage({
                  defaultMessage: 'No channels found',
                  description:
                    'Empty state header when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage: 'No channels available',
                  description:
                    'Empty state header when no publishable job board channels are configured for the account',
                })
          }
          description={
            isSearching
              ? intl.formatMessage({
                  defaultMessage:
                    'No channels match your search. Try a different keyword.',
                  description:
                    'Empty state description when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage:
                    'There are no publishable channels configured for this account at this time. Contact your administrator to get channels set up.',
                  description:
                    'Empty state description when no publishable job board channels are configured for the account',
                })
          }
        />
      </div>
    );
  }

  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {sectionMeta.map(section => (
        <Suspense key={section.key} fallback={<Sections.Skeleton />}>
          <SectionLoader
            section={section.key}
            sectionTitle={section.title}
            sectionDescription={section.description}
            searchQuery={searchQuery}
            sortBy={sortBy}
            initialSelectedOpeningIds={initialSelectedOpeningIds}
          />
        </Suspense>
      ))}
    </div>
  );
}

export function Channels({ searchQuery, sortBy }: ChannelsContentProps) {
  const { initialSelectedOpeningIds } = useJobPostingContext();

  if (initialSelectedOpeningIds.length === 0) {
    return null;
  }

  return (
    <Suspense fallback={<Channels.Skeleton />}>
      <ChannelsContent searchQuery={searchQuery} sortBy={sortBy} />
    </Suspense>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {_range(3).map(index => (
        <Sections.Skeleton key={index} />
      ))}
    </div>
  );
};
